import { localDate } from './campaigns'

/**
 * The pure drip-scheduling engine — the part of `drips.ts` that both the
 * browser tab and the Cloudflare Function need, and the only part either of
 * them is allowed to need.
 *
 * SPLIT OUT ON PURPOSE, FOR THE SAME REASON `emailrules.ts` EXISTS.
 *
 * `drips.ts` itself opens with `import { supabase } from './supabase'` — a
 * real, eagerly-constructed client that reads `import.meta.env.
 * VITE_SUPABASE_URL` the moment the module loads, for the browser tab's own
 * `loadDrips()`. That works fine in the browser, where Vite has replaced
 * `import.meta.env.*` with real values by build time. It does not work in a
 * Cloudflare Pages Function: `import.meta.env` is not defined there at all,
 * so importing ANYTHING from `drips.ts` — even a single pure function,
 * because a module's side effects run in full regardless of which export you
 * asked for — throws `Cannot read properties of undefined (reading
 * 'VITE_SUPABASE_URL')` at publish time and takes the whole deploy down with
 * it. Measured, not guessed: this is exactly what happened trying to import
 * `dueReviewedTouches` from `drips.ts` into `sync-metabase.ts` directly,
 * 2026-09-08.
 *
 * So the scheduling engine lives here, on its own, with no import that was
 * not already proven safe in the Function — `localDate` from `campaigns.ts`
 * only, which `milestone-drafts.ts` already carries into the Function bundle
 * today. `drips.ts` re-exports everything below so nothing importing from
 * `./drips` — `Drips.tsx`, `navcounts.ts`, the tests — has to change; only
 * `sync-metabase.ts` imports this file directly.
 *
 * Everything here is deliberately pure and deliberately boring: no clock
 * read, no database touched, every decision a function of arguments the
 * caller passes in. `dueTouches` answers "what is due", nothing more.
 */

export type DripLane = 'reviewed' | 'blast'
export type DripStatus = 'active' | 'paused' | 'finished'

export type DripEnrolmentMode = 'manual' | 'trigger'

export type DripCampaign = {
  id: string
  name: string
  lane: DripLane
  enrolment: DripEnrolmentMode
  trigger_key: string | null
  /**
   * The day a trigger campaign starts listening, `YYYY-MM-DD`. Null on a manual
   * campaign, and the database refuses either one the other way round.
   *
   * It gates AGENTS BY THEIR JOIN DATE, not the campaign by today's date. The
   * New Joiners card is a 90-day window, so a trigger campaign without this
   * would welcome everybody who joined in the last quarter on its first run.
   */
  listens_from: string | null
  status: DripStatus
  created_at: string
}

export type DripTouch = {
  id: number
  campaign_id: string
  step: number
  /** Days after the PREVIOUS touch. Step 1 is always 0. */
  delay_days: number
  subject: string
  body: string
  template_key: string | null
}

export type DripEnrolment = {
  id: number
  campaign_id: string
  agent_id: string
  /** This agent's day zero, `YYYY-MM-DD`. */
  started_on: string
  status: 'active' | 'stopped' | 'finished'
  stopped_reason: string | null
}

/** The trigger string a campaign's drafts are filed under. */
export const dripTrigger = (campaignId: string) => `drip:${campaignId}`

/** The occurrence string for one step. Half of the uniqueness key. */
export const stepOccurrence = (step: number) => `s${step}`

/**
 * Is this draft row part of a drip campaign, and which one?
 *
 * Matched on the prefix rather than by looking the id up, so a campaign that
 * has been deleted still has its history identifiable. `null` for milestone and
 * manual rows.
 */
export function campaignIdOf(trigger: string): string | null {
  return trigger.startsWith('drip:') ? trigger.slice('drip:'.length) : null
}

// ----------------------------------------------------------------- the clock

/**
 * Add days to a `YYYY-MM-DD` date and get another `YYYY-MM-DD`.
 *
 * Built from the numbers rather than from a Date, and this is not fussiness.
 * `new Date('2026-03-07')` is midnight UTC, so in Eastern time it is already
 * the 6th; add 30 days to that and the answer is a day early, every time, for
 * everybody. Constructing a local Date from the parts and reading it back with
 * `localDate` keeps the arithmetic in the same calendar Renee is looking at.
 *
 * The month-end and leap-year cases come free: `new Date(2027, 1, 30)` is
 * 2 March, which is what the platform's own calendar says and what anybody
 * counting on their fingers would say too.
 */
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number)
  if (!y || !m || !d) return date
  return localDate(new Date(y, m - 1, d + days))
}

/** Whole days from `a` to `b`, both `YYYY-MM-DD`. Negative if `b` is earlier. */
export function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number)
  const [by, bm, bd] = b.split('-').map(Number)
  // Midday, so a daylight-saving shift of an hour cannot round the difference
  // to the wrong whole day. The clocks move at 2am; nothing moves at noon.
  const from = new Date(ay, am - 1, ad, 12).getTime()
  const to = new Date(by, bm - 1, bd, 12).getTime()
  return Math.round((to - from) / 86_400_000)
}

/**
 * When each touch falls for one agent, given their day zero.
 *
 * Delays are cumulative because they are expressed relative to the previous
 * touch — "another in 30 days and another 30 days after that" — so step 3 of
 * (0, 30, 30) is day 60, not day 30.
 *
 * Touches are sorted by step first. A caller handing them over in id order,
 * which is what a plain `.select()` returns, would otherwise schedule the
 * sequence in whatever order the rows happened to be written.
 */
export function scheduleFor(
  startedOn: string,
  touches: readonly DripTouch[],
): { step: number; due: string }[] {
  const ordered = [...touches].sort((a, b) => a.step - b.step)
  const out: { step: number; due: string }[] = []
  let offset = 0
  for (let i = 0; i < ordered.length; i++) {
    // The first touch is day zero whatever its `delay_days` says. A campaign
    // whose first touch was saved with a delay would otherwise start late and
    // nothing on screen would explain the gap.
    offset += i === 0 ? 0 : ordered[i].delay_days
    out.push({ step: ordered[i].step, due: addDays(startedOn, offset) })
  }
  return out
}

export type DueTouch = {
  campaignId: string
  step: number
  /** Agents whose turn it is, and how overdue each one is in days. */
  agents: { agentId: string; due: string; overdueDays: number }[]
}

/**
 * What is due today, across one campaign.
 *
 * `today` and `alreadySent` are arguments rather than lookups: this function is
 * the one thing in the feature that decides who gets an email, and a test has
 * to be able to put it on any date with any history behind it.
 *
 * **Overdue counts as due.** She is away for a week; every touch that fell
 * while she was out is still waiting when she comes back, rather than having
 * quietly expired. `overdueDays` is carried so the screen can say "this was due
 * five days ago" instead of pretending it is due today.
 *
 * **A step is only due once the step before it has actually gone out.** Not
 * merely once its date has passed — actually gone. Otherwise a fortnight away
 * from the app comes back as touches 2, 3 and 4 all at once, three emails in
 * one morning to somebody who has had none, in an order that reads as a system
 * catching up with itself. One touch per agent per pass; the rest follow.
 *
 * A paused or finished campaign yields nothing. A stopped or finished enrolment
 * yields nothing. Both are checked here rather than by the caller, because
 * "which campaigns are running" is exactly the question a caller gets wrong.
 */
export function dueTouches(
  campaign: Pick<DripCampaign, 'id' | 'status'>,
  touches: readonly DripTouch[],
  enrolments: readonly DripEnrolment[],
  /** `${agentId} ${step}` for every touch already written. */
  alreadySent: ReadonlySet<string>,
  today: string,
): DueTouch[] {
  if (campaign.status !== 'active') return []
  const ordered = [...touches].sort((a, b) => a.step - b.step)
  if (ordered.length === 0) return []

  const byStep = new Map<number, DueTouch['agents']>()

  for (const e of enrolments) {
    if (e.status !== 'active') continue
    const schedule = scheduleFor(e.started_on, ordered)

    for (const { step, due } of schedule) {
      if (alreadySent.has(`${e.agent_id} ${step}`)) continue
      // The first step this agent has not had. Everything after it waits for
      // this one to go out, however overdue it is.
      if (due <= today) {
        const list = byStep.get(step) ?? []
        list.push({ agentId: e.agent_id, due, overdueDays: daysBetween(due, today) })
        byStep.set(step, list)
      }
      break
    }
  }

  return [...byStep.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([step, agents]) => ({ campaignId: campaign.id, step, agents }))
}

/**
 * Everything due right now, across every active REVIEWED-lane campaign —
 * the server's list of what to write into the Outbox today.
 *
 * Restricted to the reviewed lane on purpose. `approve()` in `Drips.tsx`
 * refuses a blast campaign outright — the button is not even rendered for
 * one — specifically because resolving a blast audience under the reviewed
 * lane's suppression would email every agent tagged `no-bulk-email` exactly
 * because they asked not to be. Auto-approval has to make the identical
 * refusal rather than becoming the one path that doesn't; there is no
 * "auto-approve a blast" here at all, not even a disabled one.
 *
 * `sent` is the same shape `loadDrips()` builds client-side — `${campaignId}
 * ${agentId} ${step}` for every draft this feature has ever written, of any
 * status — so a caller reads the ledger once (a single query server-side,
 * scoped to just the campaigns being considered) and this decides for every
 * campaign from it, the way `Drips.tsx` and `countDueDripTouches` each loop
 * per-campaign and slice `sent` down to that campaign's own keys.
 */
export function dueReviewedTouches(
  campaigns: readonly Pick<DripCampaign, 'id' | 'status' | 'lane'>[],
  touches: readonly DripTouch[],
  enrolments: readonly DripEnrolment[],
  sent: ReadonlySet<string>,
  today: string,
): DueTouch[] {
  const out: DueTouch[] = []
  for (const c of campaigns) {
    if (c.status !== 'active' || c.lane !== 'reviewed') continue
    const cTouches = touches.filter((t) => t.campaign_id === c.id)
    const cEnrolments = enrolments.filter((e) => e.campaign_id === c.id)
    const sentFor = new Set<string>()
    for (const key of sent) {
      const [cid, agentId, step] = key.split(' ')
      if (cid === c.id) sentFor.add(`${agentId} ${step}`)
    }
    out.push(...dueTouches(c, cTouches, cEnrolments, sentFor, today))
  }
  return out
}
